import { useState, useEffect } from 'react';
import { useSpring } from 'react-spring';
import { useDrag } from '@use-gesture/react';

interface UseSwipeDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  threshold?: number; // Percentage of screen width required to trigger drawer open/close
  maxWidth?: number; // Maximum width of the drawer in pixels
}

export function useSwipeDrawer({
  isOpen,
  setIsOpen,
  threshold = 0.3, // Default threshold - 30% of screen width
  maxWidth = 300, // Default max width of drawer
}: UseSwipeDrawerProps) {
  const [drawerWidth, setDrawerWidth] = useState(maxWidth);

  // Calculate drawer width on mount and window resize
  useEffect(() => {
    const updateWidth = () => {
      const windowWidth = window.innerWidth;
      setDrawerWidth(Math.min(windowWidth * 0.85, maxWidth));
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [maxWidth]);

  // Create spring animation for the drawer transform
  // Use negative window width to ensure it's completely off-screen when closed
  const [{ x }, api] = useSpring(() => ({
    x: isOpen ? 0 : -window.innerWidth,
    config: { tension: 300, friction: 30 },
  }));

  // Update spring animation when isOpen changes
  useEffect(() => {
    // Always use negative screen width to fully hide drawer
    api.start({ x: isOpen ? 0 : -window.innerWidth });
  }, [isOpen, api]);

  // Set up drag gesture handler
  const bind = useDrag(
    ({ movement: [mx], last, down, velocity: [vx], type, event }) => {
      // Log gesture events in the drawer
      console.log(
        `🧲 Drawer: ${isOpen ? 'Open' : 'Closed'}, Type: ${type}, Movement: ${mx.toFixed(2)}, Event type: ${event.type}`
      );

      // Don't track opposite direction swipes
      // If drawer is closed (not visible), only track swipes to the right
      if (!isOpen && mx <= 0) {
        console.log('⛔ Drawer (closed): Ignoring leftward swipe');
        return;
      }

      // If drawer is open, only track swipes to the left
      if (isOpen && mx >= 0) {
        console.log('⛔ Drawer (open): Ignoring rightward swipe');
        return;
      }

      // When the user releases their finger/cursor
      if (last) {
        console.log(
          `✋ Drawer: Swipe released, Movement: ${mx.toFixed(2)}, Velocity: ${vx.toFixed(2)}`
        );

        // Calculate the threshold distance - use a lower threshold for closing (easier to close)
        const openThresholdDistance = drawerWidth * threshold;
        const closeThresholdDistance = drawerWidth * (threshold / 1.5);

        // Different thresholds for opening vs closing
        const thresholdDistance = isOpen ? closeThresholdDistance : openThresholdDistance;
        console.log(
          `📏 Drawer: Threshold distance: ${thresholdDistance.toFixed(2)}, Movement: ${Math.abs(mx).toFixed(2)}`
        );

        const isOverThreshold = Math.abs(mx) > thresholdDistance;
        // Reduce velocity threshold for easier activation
        const isHighVelocity = Math.abs(vx) > 0.3;

        // Determine if we should toggle the drawer state
        const shouldToggle = isOverThreshold || isHighVelocity;
        console.log(
          `📊 Drawer: Over threshold: ${isOverThreshold}, High velocity: ${isHighVelocity}, Should toggle: ${shouldToggle}`
        );

        // If drawer is closed and swipe is rightward and past threshold
        if (!isOpen && shouldToggle && mx > 0) {
          console.log('🚪 Drawer: Opening after swipe');
          setIsOpen(true);
        }
        // If drawer is open and swipe is leftward and past threshold
        else if (isOpen && shouldToggle && mx < 0) {
          console.log('🚪 Drawer: Closing after swipe');
          setIsOpen(false);
        }
        // Otherwise, revert to current state
        else {
          console.log('🔙 Drawer: Reverting to current state');
          api.start({
            x: isOpen ? 0 : -drawerWidth,
            immediate: false,
            config: { tension: 300, friction: 26 }, // More responsive spring
          });
        }
      }
      // While dragging (not released yet)
      else if (down) {
        // Calculate the new position - always use window width for consistency
        const screenWidth = window.innerWidth;
        const newX = isOpen ? mx : -screenWidth + mx;

        // Constrain the position to prevent dragging beyond limits
        const clampedX = Math.max(-screenWidth, Math.min(0, newX));

        // Update the spring animation to follow finger position
        api.start({
          x: clampedX,
          immediate: true,
        });
      }
    },
    {
      // Apply drag only to horizontal movements
      axis: 'x',
      // Use touch events for mobile
      pointer: { touch: true },
      // Filter out taps
      filterTaps: true,
      // No delay to make it feel more responsive
      delay: 0,
    }
  );

  return {
    x,
    bind,
    drawerWidth,
  };
}
